/**
 * InstantChatLanding — Activation landing page.
 *
 * Iter XX.103 — Rebuilt around one principle: make a serious
 * cricketer (or parent of one) feel understood within five seconds.
 * Every element must answer one of four questions:
 *   (1) Do you understand how I feel?          → EMPATHY block
 *   (2) What does Gully actually do?           → PREVIEW conversation
 *   (3) Why is it different?                   → PREVIEW conversation
 *   (4) What's the easiest possible way to start? → CHIPS + composer
 *
 * The preview conversation SHOWS Gully's forensic-coach voice —
 * asking a situational question ("what's usually the score…") that
 * no coaching video or generic chatbot would ask. That single beat
 * is the whole differentiator. We stopped describing Gully and
 * started showing it.
 *
 * Interaction model unchanged from XX.83:
 *   Chips are the PRIMARY action — tap immediately sends the
 *   message and starts the conversation. Typing remains a
 *   secondary path. The video chip deep-links into /chat's upload
 *   picker.
 *
 * Analytics:
 *   · landing_page_viewed         — ClarityProvider on mount
 *   · hero_rendered               — mount (landing_variant tagged)
 *   · hero_engaged_2s             — 2s ≥50% visibility
 *   · empathy_line_viewed         — Q1 element visible
 *   · preview_conversation_viewed — Q2+Q3 element ≥5s engaged
 *   · starter_chips_viewed        — Q4 element visible
 *   · starter_chip_clicked        — chip tap
 *   · first_message_sent          — landing_variant tagged
 *   · conversation_started        — fired by Chat.jsx on paint
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { track } from "../lib/clarity";
import { buildPrewarmAttribution } from "../lib/webAttribution";
import LiveBreakthroughToast from "../components/LiveBreakthroughToast";
import { trackOnce, onceVisible, onceEngagedFor, applySessionTags } from "../lib/claritySession";
import {
  loadConfig as loadBreakthroughs,
  pickMessage as pickBreakthroughMessage,
  pickForChip as pickBreakthroughForChip,
  trackShown as trackBreakthroughShown,
  hasShownThisSession,
} from "../lib/liveBreakthroughs";
import { trackOnce as clarityTrackOnce } from "../lib/claritySession";
import ParentEntryGate from "../components/ParentEntryGate";
import {
  shouldShowParentEntryGate,
  stashPendingIntent,
  markParentGateCompleted,
  isParentGateCompleted,
} from "../lib/parentEntryGate";
import { useFlag } from "../lib/featureFlags";
import YourCricketCard from "../components/YourCricketCard";
import {
  captureParentAudienceFromUrl,
  isParentAudience,
} from "../lib/parentAudience";
import {
  getLandingPageConfig,
  HOME_LANDING_PAGE_ID,
  HERO_INTRO_LINE,
  HERO_SUBLINE_LINE,
  PLUS_SECONDARY_LINK_LABEL,
} from "../data/landingPages";
import { trackFunnel } from "../lib/activationFunnel";
import {
  getEntryExperiment,
  persistEntryVariant,
} from "../lib/entryExperiment";
import { FOCUSED_CHIPS, hasFocusedLayer, composeFocusedMessage } from "../data/focusedChips";
import { isNative } from "../lib/nativePlatform";

// Iter XX.103 — Landing rebuilt around one principle:
// make a serious cricketer (or parent of one) feel understood
// within 5 seconds. Every element must answer one of four
// questions:
//   (1) Do you understand how I feel?
//   (2) What does Gully actually do?
//   (3) Why is it different?
//   (4) What's the easiest possible way to start?
//
// Elements removed (didn't answer any of the four): the marketing
// trust stat, the "next match" opener card, the type-or-chip
// divider label, and the rotating composer placeholder. See git
// history for the previous strings — they are intentionally NOT
// referenced in this file so a search for them stays clean.
//
// Elements added:
//   · Two-line empathy block at the top — cricket-specific
//      recognition, not motivation
//   · Static 4-bubble preview conversation that SHOWS Gully's
//      forensic-coach voice (asks about situation, not mechanics)
//   · `landing_variant: "preview_v1"` property on the funnel
//      events so future iterations can be compared in Clarity
//   · `preview_conversation_viewed` Clarity event (5s of ≥50%
//      visibility on the preview element)
// Iter XX.110 (Feb 2026) — Demonstrate, don't explain.
//
// The old empathy block was two lines of marketing prose. It was
// removed because:
//   · It described Gully instead of showing it.
//   · Founder rule: "Do not tell users Gully is intelligent — show
//     it. The conversation itself should prove it."
//
// The hero is now a live 4-turn coaching demonstration that runs on
// its own the moment the page paints. Bubbles fade in one-by-one
// with beats between them so it feels like a real chat, not a
// static screenshot. Any tap anywhere on the demo (or focusing the
// composer) skips instantly to the final state — interaction is
// never delayed.
//
// Copy is founder-verbatim. Do not rewrite without founder sign-off.
const LANDING_VARIANT = "demo_v1";

// Iter XX.115 — Per-bucket content lives in `data/landingPages.js`.
// Each of these constants is now a DEFAULT (used when the page is
// mounted with no explicit `landingPageId`, i.e. the `/` homepage).
// A bucket page passes `landingPageId="performance"` etc. and reads
// its own values from the config module.
const _HOME_CFG = getLandingPageConfig(HOME_LANDING_PAGE_ID);
const HERO_HEADING = _HOME_CFG.heading;
const HERO_SUBHEADING = _HOME_CFG.subheading;

// Iter XX.110 (XX.115: default) — Founder-verbatim coaching demo.
const DEMO_CONVERSATION = _HOME_CFG.demo_conversation;

// Iter XX.110 — Bubble reveal cadence. Total ≈ 3s so the visitor
// can read the whole exchange within the "under 10 seconds"
// success window. First bubble is up on first paint (index 0
// starts visible); subsequent bubbles reveal at the offsets below.
const DEMO_REVEAL_OFFSETS_MS = [0, 900, 1900, 2800];

// Iter XX.110 — Founder trust row copy. Kept as data so the
// row can be re-styled without touching JSX.
const FOUNDER_NAME = "Built by Shivam Bajaj";
const FOUNDER_ROLE = "Competitive cricketer";
const FOUNDER_HANDLE = "@secondinnings.spinner";
const FOUNDER_INSTAGRAM_URL = "https://www.instagram.com/secondinnings.spinner";

// Iter XX.115 — Composer placeholder now sourced per-bucket from
// `data/landingPages.js`. Homepage default remains XX.110's line.
const COMPOSER_PLACEHOLDER = _HOME_CFG.composer_placeholder;

// Iter XX.83 — Chips are the PRIMARY action. Tap = immediate send.
// Copy chosen for pain-first recognition, matching the founder's
// four chip labels exactly.
//
// Iter XX.108 (Aug 2026) — Parent-audience chip variant. Founder
// rule: parents must not see player-language chips. When the
// visitor's audience is `parent` (Meta parent campaign or
// `?audience=parent` on the URL), the observation-shaped
// parent chips render instead. The chip `id` stays stable so
// downstream funnel analytics keep working across variants.
const CHIPS_PLAYER = [
  // Iter XX.254 — `starter_id` is a deterministic backend hook keyed
  // ONLY on this explicit chip tap (never on typed text). Matches the
  // `_FORCE_FORMAT_STARTERS` whitelist in `web_chat.py`. Not present
  // on other chips — the format-clarification is only sensible for
  // "getting out" prompts where T20 vs 40/50-over vs multi-day
  // changes advice materially.
  { id: "got_out",      emoji: "🏏", label: "I keep getting out",
    starter_id: "keep_getting_out" },
  { id: "bowling",      emoji: "🎯", label: "My bowling isn't working" },
  // Iter XX.160 — Video chip restored as a first-class starting
  // action for the player audience. Reuses the existing video-upload
  // flow via `/chat?from=activation&open=video` (see pickChip's
  // `chip.isVideo` branch). NO new uploader; NO backend change.
  { id: "video",        emoji: "📹", label: "Review my cricket video", isVideo: true },
  { id: "confidence",   emoji: "😔", label: "I've lost confidence" },
  { id: "parent_worry", emoji: "👨‍👦", label: "I'm worried about my child" },
];
// Iter XX.118 — Broader parent-chip set. The old chips were
// symptom-narrow ("child keeps getting out", "bowling isn't
// working"); the new /parents hero welcomes every parent, so
// the chips must invite every parent's reason for arriving —
// improvement, performance, confidence, and video review.
// The chip `id` stays stable so downstream funnel analytics keep
// working across variants; only labels/emojis change.
const CHIPS_PARENT = [
  { id: "got_out",     emoji: "🏏", label: "My child wants to improve" },
  { id: "bowling",     emoji: "📉", label: "They’re not performing" },
  { id: "confidence",  emoji: "😔", label: "They’ve lost confidence" },
  { id: "video",       emoji: "🎥", label: "Review my child’s cricket", isVideo: true },
];
// Default export kept for any downstream code that still imports
// `CHIPS` by name — resolves at runtime via `chipsForAudience()`.
const CHIPS = CHIPS_PLAYER;

export default function InstantChatLanding({ landingPageId = HOME_LANDING_PAGE_ID } = {}) {
  // Iter XX.115 — Bucket landing pages. `landingPageId` picks the
  // config (heading, subheading, demo conversation, placeholder,
  // audience-tag). Missing / unknown ids fall back to home. Every
  // analytics event carries `landing_page_id` so Meta funnel
  // dashboards can compare buckets against each other and against
  // the homepage.
  const _cfg = getLandingPageConfig(landingPageId);
  const _hero_heading = _cfg.heading || HERO_HEADING;
  const _hero_subheading = _cfg.subheading || HERO_SUBHEADING;
  const _demo_conversation = _cfg.demo_conversation || DEMO_CONVERSATION;
  const _composer_placeholder = _cfg.composer_placeholder || COMPOSER_PLACEHOLDER;
  // Iter XX.120 — "Sell the journey" section. Optional per-bucket
  // config; when missing, the block simply doesn't render. Fires the
  // `journey_preview_viewed` funnel event on first ≥50% visibility so
  // Meta can measure the emotional-bridge → first-message lift.
  const _journey = _cfg.journey || null;
  // Parent audience is either explicit config (`audience: "parent"`)
  // OR still auto-detected from `?audience=parent` on the URL.
  const _cfg_forces_parent = _cfg.audience === "parent";
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const sentRef = useRef(false);
  const composerRef = useRef(null);

  // Iter XX.110 — Demo conversation reveal state. Bubbles fade in
  // one at a time. `revealCount` = number of bubbles currently
  // visible (starts at 1 so the first bubble is up on paint and
  // there is no empty flash). A tap anywhere on the demo or the
  // composer forces `revealCount` to the full length instantly —
  // interaction must never wait for the animation.
  const [revealCount, setRevealCount] = useState(1);
  const revealTimersRef = useRef([]);

  // Iter XX.143 — Verified proof point. Fetched on mount from
  // `/api/public/landing-proof`. If the returned `count` is null (below
  // the display floor), the compact proof line falls back to a single
  // short quote from `_cfg.journey.trustStory`. Never invents a number.
  const [proofCount, setProofCount] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/public/landing-proof");
        const n = res?.data?.count;
        if (alive && typeof n === "number" && n >= 100) setProofCount(n);
      } catch { /* fail silently → fallback to testimonial */ }
    })();
    return () => { alive = false; };
  }, []);

  const skipDemo = () => {
    // Idempotent — safe to call from tap / focus / typing.
    try {
      revealTimersRef.current.forEach((t) => clearTimeout(t));
    } catch { /* noop */ }
    revealTimersRef.current = [];
    setRevealCount(_demo_conversation.length);
  };

  // Iter XX.96 — Parent Entry gate is now shown at the "commitment
  // moment" (chip tap or Send) rather than on /chat mount. We stash
  // the pending intent locally + in sessionStorage so the gate can
  // hand off the original message to /chat without loss.
  //   pendingIntentRef      — { text, source, kind } while the gate
  //                            is up. Consumed on gate onComplete.
  //   showParentGate        — controls the ParentEntryGate overlay.
  //   parentGateSessionId   — best-effort session id passed to the
  //                            gate for /web/track analytics.
  //                            Pre-warmed only when the visitor is
  //                            already eligible for the gate so we
  //                            don't create empty sessions.
  const pendingIntentRef = useRef(null);
  const [showParentGate, setShowParentGate] = useState(false);
  const [parentGateSessionId, setParentGateSessionId] = useState(null);

  // Iter XX.172 — Focused Entry Chips experiment (Feb 2026).
  //
  // Assigns 50/50 CONTROL vs FOCUSED on first landing paint, sticky
  // per browser_id across sessions/tabs (see `entryExperiment.js`).
  //
  //   CONTROL — existing one-tap broad chip → immediate send.
  //   FOCUSED — broad chip reveals a vertical stack of situational
  //             choices; second tap sends "<broad> — <focused>."
  //
  // `focusedBroadChip` holds the currently-open broad chip when the
  // FOCUSED arm has surfaced the second beat. `null` means the
  // broad-chip row is showing. `entryExperimentRef` freezes the
  // assignment for the life of this mount so a mid-journey change
  // to localStorage never flips the arm.
  const entryExperimentRef = useRef(null);
  const [focusedBroadChip, setFocusedBroadChip] = useState(null);
  const focusedShownEventFiredRef = useRef({});
  useEffect(() => {
    try {
      const { variant, reason } = getEntryExperiment();
      entryExperimentRef.current = variant;
      persistEntryVariant(variant);
      // Emit an assignment ping so the founder dashboard can see
      // arm distribution without waiting for a chip tap.
      try {
        track("entry_experiment_assigned", {
          entry_experiment: variant,
          reason,
          landing_page_id: landingPageId,
        });
      } catch { /* noop */ }
    } catch {
      entryExperimentRef.current = "control";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Iter XX.98 — Parent-first onboarding cohort.
  //
  //   parentFirstFlag  — /api/flags/me `parent_first_onboarding_enabled`.
  //                      Default OFF; enabled only for the parent Meta
  //                      campaign via /api/admin/flags.
  //   parentAudience   — memoised at mount from `?audience=parent`
  //                      (captured + persisted to localStorage). Stays
  //                      true across page reloads within the browser
  //                      so returning campaign visitors keep the
  //                      parent-first experience even after the query
  //                      string is stripped.
  //
  // The FIRST-FRAME gate renders BEFORE the hero when BOTH the flag
  // AND the audience marker are true (and `shouldShowParentEntryGate`
  // says the visitor is otherwise eligible). Otherwise the existing
  // XX.96 commit-moment gate remains untouched.
  const parentFirstFlag = useFlag("parent_first_onboarding_enabled");
  // Iter XX.156 — "Your Cricket" landing reposition flag. When ON,
  // the hero switches from marketing heading/subline to the
  // positioning headline + "Your Cricket" object card. Composer,
  // chips, and every downstream flow (parent gate, activation
  // funnel, chip taps, chat handoff) are UNCHANGED. Flag OFF
  // renders the pre-XX.156 landing byte-identical.
  const yourCricketOn = useFlag("your_cricket_landing_enabled");
  const [parentAudience, setParentAudience] = useState(false);
  const [firstFrameGate, setFirstFrameGate] = useState(false);
  const firstFrameEvaluatedRef = useRef(false);

  // Iter XX.103 — Placeholder is a single frozen line
  // (see COMPOSER_PLACEHOLDER). The prior XX.90 rotation logic was
  // removed as part of the "every element must earn its place"
  // audit — a rotating placeholder splits attention with the
  // preview conversation, and the preview is the higher-signal
  // element.

  // ------------------------------------------------------------------
  // Iter XX.98 — Parent-first-frame gate evaluation.
  //
  // Runs once on mount:
  //   1. Capture `?audience=parent` (if present) into localStorage
  //      so returning visits within the same browser retain the
  //      parent cohort even after the query string is dropped.
  //   2. Memoise the persisted `parentAudience` marker into state.
  //   3. Whenever both `parentFirstFlag` AND `parentAudience` are
  //      true — AND the visitor is still eligible for the gate —
  //      surface the first-frame gate BEFORE the hero renders.
  //
  // `firstFrameEvaluatedRef` ensures we only start the gate ONCE per
  // mount: if the visitor completes and re-navigates to the landing,
  // `shouldShowParentEntryGate()` correctly returns false and we skip.
  // ------------------------------------------------------------------
  useEffect(() => {
    // 1 + 2: URL capture + memoise persisted marker.
    try { captureParentAudienceFromUrl(); } catch { /* noop */ }
    // Iter XX.115 — Bucket config can FORCE the parent audience
    // (e.g. `/parents`, `/junior-parents`) even without the
    // `?audience=parent` URL param.
    try {
      setParentAudience(_cfg_forces_parent || isParentAudience());
    } catch { /* noop */ }
    // Iter XX.117 (Feb 2026) — Bucket-audience bypass.
    // When the landing page config explicitly declares the audience
    // (e.g. `/parents`, `/junior-parents`), the visitor has already
    // self-selected via the ad they clicked. Showing the audience
    // selector breaks the ad→landing continuity the founder wants.
    // Auto-persist parent mode ONCE so:
    //   · The first-frame gate below sees `isParentGateCompleted()`
    //     is true and skips its overlay.
    //   · The commit-moment gate in pickChip / handleTypedSend also
    //     skips (same `shouldShowParentEntryGate` check).
    //   · Chat.jsx picks up the parent relationship on first turn.
    // We deliberately do NOT persist a `player_name` — that field
    // stays null; the LLM directive tolerates missing child name
    // and Gully will ask for it naturally in-conversation.
    if (_cfg_forces_parent) {
      try {
        if (!isParentGateCompleted()) {
          markParentGateCompleted({ relationship: "parent" });
        }
      } catch { /* noop */ }
    }
  }, [_cfg_forces_parent]);
  useEffect(() => {
    // Iter XX.142 — First-frame parent gate retired. The founder
    // rule is: never block coaching for a profile question. Parent
    // context is inferred from the campaign route + landing page id,
    // then confirmed by conversational language on turn 1.
    return;
  }, [parentFirstFlag, parentAudience]);

  // Iter XX.98 — First-frame gate completion. Mirrors the commit-
  // moment handler but WITHOUT any pending intent — the visitor
  // hasn't committed to a message yet. Simply persist the mode
  // (already done inside ParentEntryGate) and reveal the hero.
  function _onFirstFrameGateComplete(mode) {
    setFirstFrameGate(false);
    // Emit the retained `parent_gate_completed` funnel event so the
    // XX.96 dashboard keeps working. `continued_to_chat` is false
    // here — the visitor lands back on the hero and still needs to
    // commit via chip/typed Send.
    try {
      const relationship = (mode && mode.relationship) || null;
      const namePresent = Boolean(
        mode && mode.player_name
        && String(mode.player_name).trim().length > 0
      );
      track("parent_gate_completed", {
        entry_mode: relationship,
        trigger: "first_frame",
        name_provided: namePresent,
        continued_to_chat: false,
      });
    } catch { /* noop */ }
  }

  // ------------------------------------------------------------------
  // Iter XX.84 — Live breakthroughs (empathetic proof).
  // Config loaded once. Generic toast scheduled 3-5s after mount.
  // Tailored toast (chip-tap) is a SEPARATE moment — never overlaps
  // the generic one, cancels the generic pending timer, capped at
  // 300ms delay so the tap feels responsive.
  // ------------------------------------------------------------------
  const [bConfig, setBConfig] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastVisible, setToastVisible] = useState(false);
  const genericTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const shownOnceRef = useRef(hasShownThisSession());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cfg = await loadBreakthroughs();
      if (cancelled) return;
      setBConfig(cfg);
      if (!cfg || !cfg.enabled || shownOnceRef.current) return;
      // Schedule the generic toast. Cleared if the user types or
      // taps a chip before it fires.
      genericTimerRef.current = setTimeout(() => {
        const msg = pickBreakthroughMessage(cfg, "generic");
        if (!msg) return;
        shownOnceRef.current = true;
        setToastMessage(msg);
        setToastVisible(true);
        trackBreakthroughShown({
          category: msg.category,
          variant: msg.variant,
          page: "activation_landing",
          sessionId: null,
        });
        // Auto-hide after visible_ms.
        hideTimerRef.current = setTimeout(() => {
          setToastVisible(false);
        }, cfg.timing.visible_ms);
      }, cfg.timing.generic_delay_ms);
    })();
    return () => {
      cancelled = true;
      if (genericTimerRef.current) clearTimeout(genericTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  // Cancel any pending generic toast when the user starts typing.
  // If the generic toast is already visible we let it finish — it
  // doesn't block interaction and the founder rule was "disappears
  // immediately if the user begins typing" so we hide it early too.
  function cancelGenericAndDismiss() {
    if (genericTimerRef.current) {
      clearTimeout(genericTimerRef.current);
      genericTimerRef.current = null;
    }
    // If a toast is currently visible, force it to fade out.
    if (toastVisible) {
      setToastVisible(false);
    }
  }

  // Autofocus the composer on mount so mobile keyboards open on
  // paint if the user chooses to type instead of tapping a chip.
  useEffect(() => {
    const t = setTimeout(() => composerRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, []);

  // Iter XX.110 — Schedule the demo conversation reveal on mount.
  // First bubble is already visible (initial revealCount = 1); the
  // remaining bubbles fade in at the offsets defined above. Timers
  // are cleared on unmount OR on `skipDemo()` (tap-to-skip).
  useEffect(() => {
    const timers = [];
    for (let i = 1; i < _demo_conversation.length; i++) {
      const t = setTimeout(() => {
        setRevealCount((c) => Math.max(c, i + 1));
      }, DEMO_REVEAL_OFFSETS_MS[i] || 0);
      timers.push(t);
    }
    revealTimersRef.current = timers;
    return () => {
      timers.forEach((t) => clearTimeout(t));
    };
  }, []);

  // ------------------------------------------------------------------
  // Iter XX.87 → XX.110 — Instagram-funnel Clarity instrumentation.
  //   hero_rendered              — once, on mount
  //   hero_engaged_2s            — 2s continuous ≥50% visibility
  //   preview_conversation_viewed— once, ≥5s ≥50% visibility on the
  //                                 demo container (XX.110: the demo
  //                                 is now the whole hero, so this
  //                                 remains the strongest
  //                                 comprehension signal)
  //   starter_chips_viewed       — once, ≥50% visible
  // All events also carry `landing_variant: "demo_v1"` so future
  // landing iterations can be compared in Clarity funnels.
  //
  // XX.110 — `empathy_line_viewed` retired; the empathy block was
  // removed. No new tracking event is added for the founder trust
  // row (subtle by design; not a KPI element).
  // ------------------------------------------------------------------
  const heroRef = useRef(null);
  const previewRef = useRef(null);
  const chipsRef = useRef(null);
  // Iter XX.120 — Journey Preview section visibility instrumentation.
  const journeyRef = useRef(null);

  useEffect(() => {
    // Iter XX.103 — Pin the Clarity SESSION-level `landing_variant`
    // tag to what this landing actually serves. ClarityProvider runs
    // before the router resolves LandingSwitch, so its default tag
    // may not match the served landing on the first paint. Calling
    // applySessionTags here (idempotent — no-op if the signature is
    // unchanged) guarantees the session tag matches the DOM.
    try { applySessionTags({ landingVariant: LANDING_VARIANT }); }
    catch { /* noop — Clarity is best-effort */ }
    // Iter XX.142 — Activation funnel: `landing_view` is the top of
    // the funnel. Fired once per session per landing page.
    try {
      const audience = isParentAudience() ? "parent" : "player";
      trackFunnel("landing_view", {
        landing_page_id: landingPageId,
        audience,
        path: window?.location?.pathname || "/",
      });
    } catch { /* noop */ }
    // hero_rendered — one shot, as soon as the hero mounts.
    trackOnce("hero_rendered", {
      path: window?.location?.pathname || "/",
      landing_variant: LANDING_VARIANT,
      landing_page_id: landingPageId,
    });
    // hero_engaged_2s — 2s of continuous visibility.
    const stopEngaged = heroRef.current
      ? onceEngagedFor(heroRef.current, 2, () => {
          trackOnce("hero_engaged_2s",
                    { path: window?.location?.pathname || "/",
                      landing_variant: LANDING_VARIANT,
                      landing_page_id: landingPageId });
        })
      : null;
    // Iter XX.103 → XX.110 → XX.115 — 5s of continuous ≥50%
    // visibility on the demo is a strong proxy for "the visitor
    // actually read the coaching exchange." Every bucket landing
    // reports it independently under its own landing_page_id so
    // the founder can compare comprehension across buckets.
    const stopPreview = previewRef.current
      ? onceEngagedFor(previewRef.current, 5, () => {
          trackOnce("preview_conversation_viewed",
                    { path: window?.location?.pathname || "/",
                      landing_variant: LANDING_VARIANT,
                      landing_page_id: landingPageId });
        })
      : null;
    const stopChips = chipsRef.current
      ? onceVisible(chipsRef.current, () => {
          trackOnce("starter_chips_viewed",
                    { path: window?.location?.pathname || "/",
                      landing_variant: LANDING_VARIANT,
                      landing_page_id: landingPageId });
        })
      : null;
    // Iter XX.156 — Composer visibility beacon. Fires once when the
    // input row is on-screen (≥50% for one paint). The new "Your
    // Cricket" landing needs this to prove the composer stays
    // above-the-fold on mobile despite the added card. Fires
    // regardless of the flag so we can compare pre-XX.156 vs
    // post-XX.156 composer visibility.
    const stopComposerViewed = composerRef.current
      ? onceVisible(composerRef.current, () => {
          trackOnce("composer_viewed",
                    { path: window?.location?.pathname || "/",
                      landing_variant: LANDING_VARIANT,
                      landing_page_id: landingPageId,
                      your_cricket_landing_on: yourCricketOn });
        })
      : null;
    // Iter XX.120 — Journey Preview visibility beacon.
    // Iter XX.121 — Same observer also fires `trust_story_viewed`
    // when a trustStory is configured for this bucket, so the two
    // events stay synchronised without a second IntersectionObserver.
    const stopJourney = journeyRef.current
      ? onceVisible(journeyRef.current, () => {
          trackOnce("journey_preview_viewed",
                    { path: window?.location?.pathname || "/",
                      landing_variant: LANDING_VARIANT,
                      landing_page_id: landingPageId });
          if (_journey && _journey.trustStory && _journey.trustStory.quote) {
            trackOnce("trust_story_viewed",
                      { path: window?.location?.pathname || "/",
                        landing_variant: LANDING_VARIANT,
                        landing_page_id: landingPageId,
                        attribution: _journey.trustStory.attribution || null });
          }
        })
      : null;
    return () => {
      try { stopEngaged?.(); } catch { /* noop */ }
      try { stopPreview?.(); } catch { /* noop */ }
      try { stopChips?.(); } catch { /* noop */ }
      try { stopComposerViewed?.(); } catch { /* noop */ }
      try { stopJourney?.(); } catch { /* noop */ }
    };
  }, []);

  async function pickChip(chip) {
    if (sentRef.current || sending) return;
    // Iter XX.87 — rename to `starter_chip_clicked` per new Clarity
    // spec. `chip_key` is a fixed enum (got_out / bowling / confidence
    // / video) — never carries user text.
    track("starter_chip_clicked", { chip_key: chip.id, landing_page_id: landingPageId });
    // Iter XX.172 — `entry_chip_tapped` funnel event so the founder
    // dashboard can compute entry-completion drop-off:
    //   entry_chip_tapped (broad tap, both arms)
    //     → primary_cta_clicked (first message sent — instant in
    //       CONTROL, second tap in FOCUSED)
    // Never carries user text. Fires once per session per broad chip.
    try {
      trackFunnel("entry_chip_tapped", {
        entry_experiment: entryExperimentRef.current || "control",
        entry_problem: chip.id,
        landing_page_id: landingPageId,
      });
    } catch { /* noop */ }
    // Iter XX.156 — Additive alias. The "Your Cricket" reposition
    // spec asks for `quick_prompt_clicked` alongside the existing
    // `starter_chip_clicked` event so the new funnel dashboards can
    // key off a single, semantically-clean event name. Both events
    // fire on the same tap so no downstream analytics regress.
    try {
      track("quick_prompt_clicked", {
        chip_key: chip.id,
        landing_page_id: landingPageId,
        your_cricket_landing_on: yourCricketOn,
      });
    } catch { /* noop */ }
    // Iter XX.84 — Cancel any pending generic toast. Chips are the
    // "primary action" moment; the tailored toast (below) takes
    // over. Never show tailored while generic is on screen — the
    // two are separate moments per founder brief.
    const genericIsVisible = toastVisible;
    cancelGenericAndDismiss();
    // Iter XX.142 — Parent-entry gate removed from the chip flow.
    // Founder rule: never block coaching for a profile question.
    // Parent context is inferred from the campaign route + landing
    // page id (both stamped on `/api/web/session`), and confirmed by
    // conversational language detection on turn 1 ("my son / daughter").
    // Tailored toast: brief flash echoing the chip's problem so the
    // player feels heard before landing in the coaching thread.
    // Skipped for the video chip (that path is upload-first) AND
    // when the generic toast was still visible (avoid double-show
    // in the same beat).
    if (
      bConfig?.enabled
      && !chip.isVideo
      && !genericIsVisible
    ) {
      const tailored = pickBreakthroughForChip(bConfig, chip.id);
      if (tailored) {
        setToastMessage(tailored);
        setToastVisible(true);
        trackBreakthroughShown({
          category: tailored.category,
          variant: tailored.variant,
          page: "activation_landing_chip_tailored",
          sessionId: null,
        });
      }
    }
    // Video chip — skip the chat send entirely. Deep-link to /chat
    // with `?open=video` so the video picker opens on arrival.
    if (chip.isVideo) {
      sentRef.current = true;
      // Iter XX.87 — Chip tap is a user commitment; count it as
      // first_message_sent even though it opens the upload flow
      // instead of posting text. Dedup guards against double-count.
      clarityTrackOnce("first_message_sent", {
        source: "activation_chip:video",
        landing_variant: LANDING_VARIANT,
      });
      // Best-effort session pre-warm so /chat has a session id
      // ready when the upload flow needs one.
      try {
        // Iter XX.115 — every session-create carries the landing_page_id
        // so the backend can persist it on `web_sessions` for funnel
        // analytics and same-session Clarity tag.
        // Iter XX.297 — P-M4 fix: the pre-warm now carries first-touch
        // attribution (was: landing_page_id only → session row arrived
        // with empty UTMs and Chat.jsx hydrated it instead of running
        // its own attributed startSession). Fail-soft — a throw here
        // must never block the pre-warm.
        const s = await api.post("/web/session", {
          ...buildPrewarmAttribution(),
          landing_page_id: landingPageId,
        });
        const sid = s?.data?.session_id;
        if (sid) {
          try { localStorage.setItem("gully_web_session_id", sid); }
          catch { /* localStorage disabled */ }
        }
      } catch { /* non-fatal — /chat will create its own session */ }
      navigate("/chat?from=activation&open=video", { replace: true });
      return;
    }
    // Iter XX.172 — Focused Entry Chips experiment.
    // FOCUSED arm on a broad chip that has a focused layer: DO NOT
    // send yet. Reveal the situational choices instead. The player's
    // next tap is what sends the actual (richer) message.
    // CONTROL arm falls through to the existing immediate-send path.
    if (
      entryExperimentRef.current === "focused"
      && hasFocusedLayer(chip.id)
    ) {
      setFocusedBroadChip(chip);
      // Fire once per (broad chip, session) so we can measure the
      // second-beat impression rate without double-counting.
      const shownKey = `${chip.id}`;
      if (!focusedShownEventFiredRef.current[shownKey]) {
        focusedShownEventFiredRef.current[shownKey] = true;
        try {
          track("focused_chip_shown", {
            entry_experiment: "focused",
            entry_problem: chip.id,
            landing_page_id: landingPageId,
          });
        } catch { /* noop */ }
      }
      return;
    }
    // Ordinary chip — send the label AS-IS and enter the chat.
    // Tiny delay (≤300 ms) so the tailored toast has a beat to be
    // perceived. Capped at bConfig.timing.tailored_delay_ms which
    // is clamped to 300 in loadConfig().
    const delay = Math.min(
      300, bConfig?.timing?.tailored_delay_ms || 250,
    );
    await new Promise((r) => setTimeout(r, delay));
    // Iter XX.172 — CONTROL arm (or focused arm on a chip without a
    // focused layer, e.g. parent_worry): send exactly today's
    // behaviour and tag it as `control` for the experiment split.
    // `entry_problem` is set so measurement can group by broad chip
    // even in the control arm.
    await sendMessage(chip.label, {
      source: `activation_chip:${chip.id}`,
      entry_experiment: entryExperimentRef.current || "control",
      entry_problem: chip.id,
      // Iter XX.254 — deterministic starter hook. Passes only when
      // the chip config declares one (currently: `got_out`). Never
      // derived from label text.
      starter_id: chip.starter_id || null,
    });
  }

  // Iter XX.172 — Focused chip tap (second beat, FOCUSED arm only).
  // Composes the natural-language first message and sends. The
  // richer message is what XX.167 First-Message Aha classifies —
  // no first-response logic changes.
  //
  // "Something else" (id="other") routes to the free-text composer
  // instead of sending; the player types their own situation.
  async function pickFocusedChip(broadChip, focusedChip) {
    if (sentRef.current || sending || !broadChip || !focusedChip) return;
    try {
      track("focused_chip_selected", {
        entry_experiment: "focused",
        entry_problem: broadChip.id,
        entry_situation: focusedChip.id,
        landing_page_id: landingPageId,
      });
    } catch { /* noop */ }
    if (focusedChip.id === "other") {
      // Free-text path — clear the second beat, focus the composer,
      // prefill nothing so the player writes their own thing.
      setFocusedBroadChip(null);
      try {
        composerRef.current && composerRef.current.focus();
      } catch { /* noop */ }
      return;
    }
    const composedMsg = composeFocusedMessage(broadChip.label, focusedChip.label);
    await sendMessage(composedMsg, {
      source: `activation_chip:${broadChip.id}`,
      entry_experiment: "focused",
      entry_problem: broadChip.id,
      entry_situation: focusedChip.id,
      // Iter XX.254 — deterministic starter hook inherits from the
      // BROAD chip. The user explicitly tapped `got_out` first, then
      // narrowed with a focused chip — this remains an ID-driven
      // action, never a keyword classifier on prose.
      starter_id: broadChip.starter_id || null,
    });
  }

  async function sendMessage(rawMsg, opts = {}) {
    if (sentRef.current) return;
    const msg = (rawMsg || "").trim();
    if (!msg || sending) return;

    // Iter XX.149 — Instant-navigate landing→chat handoff.
    // Perceived-latency fix: the previous implementation blocked for
    // /web/session (~200 ms) + /web/chat (~2-6 s LLM) BEFORE
    // navigating. Visitors stared at a static landing page for
    // seconds. Now: stash the intent, navigate immediately, and let
    // Chat.jsx paint the user bubble + typing indicator while the
    // network calls happen in the background. Session creation is
    // still server-controlled (`/web/session` on the Chat.jsx side)
    // so integrity is preserved.
    sentRef.current = true;
    setSending(true);
    const t_tap = (typeof performance !== "undefined" && performance.now)
      ? performance.now() : Date.now();

    // Stash the original message + landing context so Chat.jsx can
    // resume server-side. `sentRef` above already prevents duplicate
    // fires from a rapid double-tap. Analytics fire AFTER navigate so
    // they never delay the transition.
    stashPendingIntent({
      text: msg,
      source: opts.source || "activation_typed",
      kind: "text",
      landing_page_id: landingPageId,
      t_tap,
      // Iter XX.172 — experiment/problem/situation propagated on the
      // intent so Chat.jsx can (a) forward on the first /web/chat
      // POST and (b) tag every downstream funnel event. Absent on
      // free-text sends.
      entry_experiment: opts.entry_experiment || null,
      entry_problem:    opts.entry_problem    || null,
      entry_situation:  opts.entry_situation  || null,
      // Iter XX.254 — deterministic starter hook, populated only by
      // pickChip when the chip declares an explicit starter_id.
      starter_id:       opts.starter_id       || null,
    });

    try {
      // eslint-disable-next-line no-console
      console.info("[gully.latency] landing.tap → navigate", {
        source: opts.source || "activation_typed",
        msg_len: msg.length,
        t_tap: Math.round(t_tap),
      });
    } catch { /* noop */ }

    // Navigate IMMEDIATELY. Chat.jsx picks up the pending intent on
    // mount, paints the user bubble optimistically, shows the typing
    // dots, then fires /web/chat once /web/session has resolved.
    navigate("/chat?from=activation&pending=1", { replace: true });

    // Fire the non-critical analytics off the critical path — a
    // 0-ms timeout hands them to the event loop AFTER the navigation
    // commit so they never contend with the paint.
    setTimeout(() => {
      try {
        clarityTrackOnce("first_message_sent", {
          source: opts.source || "activation_typed",
          landing_variant: LANDING_VARIANT,
        });
      } catch { /* noop */ }
      try {
        const audience = isParentAudience() ? "parent" : "player";
        trackFunnel("primary_cta_clicked", {
          entry_source: opts.source || "activation_typed",
          landing_page_id: landingPageId,
          audience,
          // Iter XX.172 — experiment metadata on the funnel event.
          // Backend `activation_funnel_events` stores this in the
          // `payload` dict so the founder dashboard can slice.
          entry_experiment: opts.entry_experiment
            || entryExperimentRef.current
            || "control",
          entry_problem:    opts.entry_problem    || null,
          entry_situation:  opts.entry_situation  || null,
        });
      } catch { /* noop */ }
    }, 0);
  }

  async function handleTypedSend() {
    const msg = (text || "").trim();
    if (!msg || sending || sentRef.current) return;
    // Iter XX.142 — Parent-entry gate removed from the landing→chat
    // path. Founder rule: never block coaching for a profile question.
    // Parent context is inferred from the campaign route + landing
    // page id (already stamped on `/api/web/session`), and confirmed
    // by conversational language detection ("my son / daughter").
    await sendMessage(msg, { source: "activation_typed" });
  }

  // Iter XX.96 — Parent Entry gate completed. The gate itself has
  // already persisted the relationship/name (markParentGateCompleted
  // → localStorage.g_parent_mode_v1) so the very first /web/chat
  // POST from /chat will pick up the parent_context. Our job here
  // is only to route the visitor to the correct destination without
  // losing their original intent.
  function _onParentGateComplete(mode) {
    setShowParentGate(false);
    const intent = pendingIntentRef.current;
    pendingIntentRef.current = null;
    // Iter XX.96 (analytics) — Fire the `parent_gate_completed`
    // funnel event exactly once here, i.e. only after the gate has
    // been *completed* (self picked OR parent + valid name). The
    // dismissal / abandonment paths never reach this callback, so
    // firing here satisfies the "fire only on completion" rule.
    // NO PII: child name is never sent — only its presence (bool).
    try {
      const relationship = (mode && mode.relationship) || null;
      const namePresent = Boolean(
        mode && mode.player_name && String(mode.player_name).trim().length > 0
      );
      const trigger = intent && intent.source
        ? (intent.source.indexOf("chip") >= 0 ? "chip" : "typed")
        : null;
      track("parent_gate_completed", {
        entry_mode: relationship,
        trigger,
        name_provided: namePresent,
        continued_to_chat: Boolean(intent),
      });
    } catch { /* noop */ }
    if (!intent) {
      // No intent captured (defensive; shouldn't happen because the
      // gate is only shown after a chip/send). Send them to /chat
      // as a neutral fallback.
      navigate("/chat?from=activation", { replace: true });
      return;
    }
    if (intent.kind === "video") {
      // Video path: /chat handles ?open=video to open the picker.
      sentRef.current = true;
      clarityTrackOnce("first_message_sent", {
        source: "activation_chip:video",
        landing_variant: LANDING_VARIANT,
      });
      navigate("/chat?from=activation&open=video", { replace: true });
      return;
    }
    // Text path: stash the pending intent so Chat.jsx can auto-fire
    // it after the XX.92 scripted intro plays (parent-aware). We
    // intentionally do NOT call /web/chat here — deferring the POST
    // to /chat lets the intro run first, matching the founder's
    // ordered flow: Parent Entry → intro → conversation begins.
    stashPendingIntent({
      text: intent.text,
      source: intent.source,
      kind: "text",
    });
    sentRef.current = true;
    navigate("/chat?from=activation&pending=1", { replace: true });
  }

  function onKeyDown(e) {
    // Enter to send on desktop; Shift+Enter (or mobile) = newline.
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      handleTypedSend();
    }
  }

  return (
    <div
      data-testid="activation-landing"
      className="min-h-[100dvh] bg-neutral-950 text-neutral-100 flex flex-col"
    >
      {/* Iter XX.98 — Parent-first onboarding. When the flag +
          `?audience=parent` cohort criteria are met, this fixed
          overlay renders BEFORE the hero. The hero stays mounted
          underneath (avoids CLS + preserves existing effects) but
          is hidden behind the gate's z-50 backdrop until the
          visitor picks Me / My child. */}
      {firstFrameGate && (
        <ParentEntryGate
          sessionId={parentGateSessionId}
          variant="first_frame"
          onComplete={_onFirstFrameGateComplete}
        />
      )}
      {/* Iter XX.84 — Ambient empathetic-proof toast. Never blocks
          interaction; disappears on typing; once per session only. */}
      <LiveBreakthroughToast
        message={toastMessage}
        visible={toastVisible}
        fadeMs={bConfig?.timing?.fade_ms || 250}
      />
      <header className="px-5 pt-3 pb-1 flex items-center">
        <span className="font-display text-lg tracking-tight">gully</span>
      </header>

      <main className="flex-1 flex flex-col px-4 sm:px-5 pt-3 pb-2 max-w-xl mx-auto w-full">
        {/* Iter XX.145 — Above-the-fold hierarchy (founder-locked):
             1. Intro line          — "Hi, I'm Gully."
             2. Hero heading        — bucket-specific curiosity hook
                                       (home: "Every match leaves clues.")
             3. One coach subline   — "Let's figure out what yours are saying."
             4. Verified proof      — humanised: "3,100+ conversations with
                                       cricketers and parents"
             5. Composer            — always shows a subtle send arrow
             6. Four one-tap chips  — fallback for people who don't know
                                       where to start
             7. Quiet Gully Plus link — for warm returners
             Founder rule: the page should feel like walking over to a
             coach after a game, not arriving at a software product.
        */}
        <div
          data-testid="activation-intro-line"
          className="text-[13px] sm:text-[14px] leading-snug text-emerald-300/85 font-medium mb-1"
        >
          {HERO_INTRO_LINE}
        </div>
        <div
          data-testid="activation-heading-block"
          className="mb-3"
        >
          {yourCricketOn ? (
            /* Iter XX.156 — Positioning headline. Replaces the
                bucket-specific curiosity heading when the flag is ON.
                Two-line, bold, no marketing language. The headline is
                the FIRST thing the visitor reads and must reframe the
                page from "another chatbot" to "I'm building my
                cricket". Copy is founder-verbatim — do not rewrite
                without founder sign-off. */
            <>
              <h1
                data-testid="activation-heading-your-cricket"
                className="text-[24px] sm:text-[28px] leading-[1.15] font-semibold text-neutral-100 tracking-tight"
              >
                Understand your game
                <br />
                <span className="text-emerald-300">like you never have before.</span>
              </h1>
              <p
                data-testid="activation-hero-subline-your-cricket"
                className="mt-2 text-[13.5px] sm:text-[14.5px] leading-snug text-neutral-400"
              >
                Gully remembers your matches, watches your videos, spots the patterns
                you miss, and helps you become a smarter cricketer over time.
              </p>
            </>
          ) : (
            <>
              <h1
                data-testid="activation-heading"
                className="text-[22px] sm:text-[26px] leading-tight font-semibold text-neutral-100 tracking-tight"
              >
                {_hero_heading}
              </h1>
              <p
                data-testid="activation-hero-subline"
                className="mt-2 text-[14px] sm:text-[15px] leading-snug text-neutral-300"
              >
                {HERO_SUBLINE_LINE}
              </p>
            </>
          )}
        </div>

        {/* Iter XX.156 — "Your Cricket" object. Visual centre of
             gravity when the flag is ON. Fetches real counts from
             /api/web/journey (Matches / Practices / Videos /
             Conversations / Lessons). Anonymous / no-data visitors
             see "Nothing built yet." Never fabricates numbers. Does
             not delay the composer — renders alongside the empty
             state on first paint, then hydrates from the API. */}
        {yourCricketOn ? <YourCricketCard hideWhenEmpty /> : null}

        {/* Iter XX.145 — Compact, humanised proof. Real count from
             THIS deployment's Mongo, rounded DOWN to the nearest 100,
             framed around the people the conversations belong to. */}
        <div
          data-testid="activation-proof-line"
          className="mb-3 text-[12px] sm:text-[12.5px] leading-snug text-neutral-500"
        >
          {proofCount ? (
            <span data-testid="activation-proof-count">
              <span className="text-emerald-400 tabular-nums">
                {proofCount.toLocaleString()}+
              </span>
              {" conversations with cricketers and parents"}
            </span>
          ) : (_journey && _journey.trustStory && _journey.trustStory.quote) ? (
            <span data-testid="activation-proof-quote" className="italic">
              “{_journey.trustStory.quote}”
            </span>
          ) : null}
        </div>

        {/* Iter XX.110 (kept, moved) — Animated coaching demonstration.
             Anchor comment retained; the DOM now sits below the fold,
             inside the `activation-below-fold` block further down. */}

        {/* Iter XX.143 — Removed duplicate heading block. The above-
             the-fold hero (intro + heading + what-Gully-does + audience
             + proof) now lives ABOVE the demo animation. */}

        {/* Iter XX.122 (Feb 2026) — Composer moved above-the-fold.
            Clarity showed the biggest funnel leak was
            landing_page_viewed → conversation_started (~12%). Root
            cause: composer sat below the fold on mobile, so visitors
            had to READ before they could ACT. The founder rule now
            is "Action before reassurance". Composer sits directly
            under the hero heading so a visitor can type their first
            message without scrolling. Reassurance (chips + journey
            card + trust quote) lives BELOW so it's discovered
            naturally when hesitation surfaces — a stronger
            psychological flow than forcing consumption first. All
            existing analytics (`primary_cta_clicked` + `first_message_sent`)
            preserved verbatim. */}
        <div className="mb-3">
          <label htmlFor="activation-composer" className="sr-only">
            Tell Gully what happened
          </label>
          <div className="flex items-end gap-2 rounded-2xl border border-neutral-800 bg-neutral-900 px-3 py-2">
            <textarea
              id="activation-composer"
              ref={composerRef}
              rows={2}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                // Iter XX.84 — begin-typing dismisses / cancels the
                // ambient toast. Never blocks input.
                if (e.target.value) cancelGenericAndDismiss();
                // Iter XX.110 — Typing also skips the demo so it
                // doesn't animate over the user's active thought.
                // Note: we deliberately do NOT skip on `onFocus`
                // because autofocus fires ~60ms after mount and
                // would kill the whole animation.
                skipDemo();
              }}
              onKeyDown={onKeyDown}
              placeholder={_composer_placeholder}
              disabled={sending}
              enterKeyHint="send"
              autoComplete="off"
              autoCapitalize="sentences"
              spellCheck
              data-testid="activation-composer"
              className="flex-1 bg-transparent text-[16px] leading-6 outline-none resize-none placeholder:text-neutral-500 disabled:opacity-50"
            />
            {/* Iter XX.145 — The composer always shows a subtle send
                 arrow. Muted when empty (visitor sees WHERE the
                 conversation begins even before typing), emerald when
                 there's text to send. Chip taps skip the button
                 entirely and start the conversation directly. */}
            <button
              type="button"
              onClick={() => {
                if (!text.trim()) return;
                track("primary_cta_clicked", {
                  cta_key: "activation_send",
                  landing_variant: LANDING_VARIANT,
                  landing_page_id: landingPageId,
                });
                handleTypedSend();
              }}
              disabled={!text.trim() || sending}
              data-testid="activation-send-btn"
              aria-label={sending ? "Sending your first message" : "Start Chatting"}
              className={
                "shrink-0 h-9 w-9 rounded-full flex items-center justify-center text-[16px] font-medium transition-colors "
                + (text.trim()
                    ? "bg-emerald-600 hover:bg-emerald-500 text-neutral-950"
                    : "bg-neutral-800 text-neutral-500 cursor-not-allowed")
              }
            >
              {sending ? (
                <span aria-hidden="true">…</span>
              ) : (
                <span aria-hidden="true">→</span>
              )}
            </button>
          </div>
        </div>

        {/* Iter XX.83 → XX.110 — Chips are still the PRIMARY quick-tap
            start path. Placed under the heading so the tap path
            visually reads as "your turn" after Gully's last question
            in the demo. Tap = immediate send.

            Iter XX.172 — When `focusedBroadChip` is set (FOCUSED arm
            has surfaced the second beat), replace the broad-chip
            row with the situational choices for that broad chip. */}
        <div
          className="flex flex-col gap-1.5 mb-2"
          data-testid="activation-chip-row"
          ref={chipsRef}
        >
          {focusedBroadChip ? (
            <>
              {/* Second-beat header — conversational, NOT categorization
                  software. Reads like Gully is already listening. */}
              <div
                data-testid="focused-chip-header"
                className="px-2 pt-1 pb-2 text-[14px] text-neutral-300"
              >
                <span aria-hidden="true" className="mr-2">{focusedBroadChip.emoji}</span>
                What&apos;s been happening?
              </div>
              {(FOCUSED_CHIPS[focusedBroadChip.id] || []).map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => pickFocusedChip(focusedBroadChip, f)}
                  disabled={sending}
                  data-testid={`focused-chip-${focusedBroadChip.id}-${f.id}`}
                  className="w-full text-left text-[14px] px-4 py-2.5 rounded-xl border border-neutral-800 bg-neutral-900/50 text-neutral-100 hover:border-neutral-600 hover:bg-neutral-900 active:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <span>{f.label}</span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => {
                  try {
                    track("focused_chip_back", {
                      entry_experiment: "focused",
                      entry_problem: focusedBroadChip.id,
                      landing_page_id: landingPageId,
                    });
                  } catch { /* noop */ }
                  setFocusedBroadChip(null);
                }}
                disabled={sending}
                data-testid="focused-chip-back"
                className="w-full text-left text-[13px] px-4 py-2 rounded-xl text-neutral-500 hover:text-neutral-300 transition-colors"
              >
                ← back
              </button>
            </>
          ) : (
            (parentAudience ? CHIPS_PARENT : CHIPS_PLAYER).map((chip) => (
              <button
                key={chip.id}
                type="button"
                onClick={() => pickChip(chip)}
                disabled={sending}
                data-testid={`activation-chip-${chip.id}`}
                className="w-full text-left text-[14px] px-4 py-2.5 rounded-xl border border-neutral-800 bg-neutral-900/50 text-neutral-100 hover:border-neutral-600 hover:bg-neutral-900 active:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <span className="mr-2 select-none" aria-hidden="true">{chip.emoji}</span>
                <span>{chip.label}</span>
              </button>
            ))
          )}
          {/* Iter XX.154.1 — Lesson Capture entry point. Routes to
              the isolated /lessons/new surface. Deliberately styled
              same as the coaching chips so it feels like part of the
              row, not a marketing add-on. Hidden for parent audience
              (feature is for the player themselves). */}
          {!parentAudience && !focusedBroadChip && (
            <button
              type="button"
              onClick={() => navigate("/lessons/new")}
              disabled={sending}
              data-testid="activation-chip-lesson-capture"
              className="w-full text-left text-[14px] px-4 py-2.5 rounded-xl border border-neutral-800 bg-neutral-900/50 text-neutral-100 hover:border-neutral-600 hover:bg-neutral-900 active:bg-neutral-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <span className="mr-2 select-none" aria-hidden="true">📖</span>
              <span>I learnt something today</span>
            </button>
          )}
        </div>

        {/* Iter XX.143 — Quiet secondary link. For warm visitors
             who already know Gully and want to subscribe directly.
             Must never visually compete with the primary CTA — the
             typography (neutral-500, small, no button chrome) is
             deliberate.
             Iter XX.194 — Hidden on Capacitor native (Android / iOS).
             Play policy: the native app must expose no Plus /
             upgrade / external-payment surface. Web experience
             unchanged. */}
        {!isNative() && (
        <div className="mb-6 pt-1 text-center">
          <a
            href="/plus"
            data-testid="activation-plus-secondary-link"
            onClick={() => {
              try {
                track("plus_secondary_link_clicked", {
                  landing_variant: LANDING_VARIANT,
                  landing_page_id: landingPageId,
                });
              } catch { /* noop */ }
            }}
            className="text-[12px] leading-snug text-neutral-500 hover:text-neutral-300 underline decoration-neutral-800 hover:decoration-neutral-500 underline-offset-2 transition-colors"
          >
            {PLUS_SECONDARY_LINK_LABEL}
          </a>
        </div>
        )}

        {/* ============================================================
             Iter XX.143 — Below-the-fold zone. Everything from here
             down is supporting content. It exists so a hesitant visitor
             can find reassurance by scrolling, but it never delays the
             first click. The animated coaching demonstration lives
             INSIDE this block now (previously above the fold).
             ============================================================ */}
        <div
          data-testid="activation-below-fold"
          className="mt-4 pt-4 border-t border-neutral-900"
        >
          {/* Iter XX.143 — Demo bubble lives below the fold. It's the
               strongest visual proof but it also took up the most
               above-fold space. Founder rule: proof lives below the
               first click, never above it. */}
          <div
            data-testid="activation-hero"
            ref={heroRef}
            className="mb-4"
          >
            <div
              ref={previewRef}
              data-testid="activation-demo"
              role="figure"
              aria-label="Live coaching conversation with Gully"
              onClick={skipDemo}
              className="rounded-2xl border border-neutral-800 bg-neutral-900/40 px-4 py-3 space-y-2 cursor-pointer select-none"
            >
              {_demo_conversation.map((b, i) => {
                const isVisible = i < revealCount;
                return (
                  <div
                    key={i}
                    data-testid={`activation-demo-bubble-${i}`}
                    data-who={b.who}
                    data-visible={isVisible ? "true" : "false"}
                    className={
                      "transition-all duration-500 ease-out "
                      + (isVisible
                          ? "opacity-100 translate-y-0"
                          : "opacity-0 translate-y-1 pointer-events-none h-0 overflow-hidden") + " "
                      + (b.who === "you"
                          ? "flex flex-col items-end"
                          : "flex flex-col items-start")
                    }
                    aria-hidden={isVisible ? "false" : "true"}
                  >
                    <span
                      className={
                        "text-[10px] uppercase tracking-[0.12em] mb-1 "
                        + (b.who === "you"
                          ? "text-neutral-500 pr-1"
                          : "text-emerald-500/80 pl-1")
                      }
                    >
                      {b.who === "you" ? "You" : "Gully"}
                    </span>
                    <div
                      className={
                        "text-[14px] sm:text-[15px] leading-relaxed px-3 py-2 rounded-2xl max-w-[92%] "
                        + (b.who === "you"
                          ? "bg-neutral-800/70 text-neutral-100 rounded-br-md"
                          : "bg-neutral-100/[0.04] border border-neutral-800 text-neutral-100 rounded-bl-md")
                      }
                    >
                      {b.text}
                    </div>
                  </div>
                );
              })}
              {revealCount < _demo_conversation.length
                && _demo_conversation[revealCount]?.who === "gully" && (
                <div
                  data-testid="activation-demo-typing"
                  aria-hidden="true"
                  className="flex flex-col items-start"
                >
                  <span className="text-[10px] uppercase tracking-[0.12em] mb-1 text-emerald-500/80 pl-1">
                    Gully
                  </span>
                  <div className="text-[14px] leading-relaxed px-3 py-2 rounded-2xl bg-neutral-100/[0.04] border border-neutral-800 rounded-bl-md">
                    <span className="inline-flex gap-1">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-neutral-400 animate-pulse" />
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-neutral-400 animate-pulse [animation-delay:0.15s]" />
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-neutral-400 animate-pulse [animation-delay:0.3s]" />
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

        {/* Iter XX.160 — Return-to-composer CTA. Clarity recordings
             showed visitors scrolling the whole landing and then
             leaving because the composer sat only at the top. This
             button lives directly after the demo (the strongest
             proof point) so a convinced visitor can start without
             scrolling back up. Reuses the composer ref + focus
             logic — NO new backend, NO new session, NO analytics
             renames. Existing `primary_cta_clicked` fires so the
             funnel dashboard aggregates this alongside the top-of-
             page send. */}
        <div
          data-testid="activation-secondary-cta-row"
          className="mb-4 flex justify-center"
        >
          <button
            type="button"
            data-testid="activation-secondary-cta"
            onClick={() => {
              try {
                track("primary_cta_clicked", {
                  cta_key: "activation_secondary_ask",
                  landing_variant: LANDING_VARIANT,
                  landing_page_id: landingPageId,
                });
              } catch { /* noop */ }
              try {
                composerRef.current?.scrollIntoView({
                  behavior: "smooth", block: "center",
                });
              } catch { /* noop */ }
              // Wait for the smooth scroll to settle before focusing
              // so mobile Safari doesn't jump-cut the viewport.
              setTimeout(() => {
                try { composerRef.current?.focus(); } catch { /* noop */ }
              }, 320);
            }}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-800/70 bg-emerald-900/25 hover:bg-emerald-900/40 active:bg-emerald-900/55 px-5 py-2 text-[13.5px] leading-none font-medium text-emerald-100 transition-colors"
          >
            <span aria-hidden="true">🏏</span>
            <span>Ask Gully</span>
          </button>
        </div>

        {/* Iter XX.120 — Journey Preview. Emotional bridge that now
            follows the composer (Iter XX.122 reorder). A visitor who
            hesitates at the composer naturally scrolls to see WHY
            they should continue — reassurance in the moment they
            actually want it, rather than blocking action.
            Deliberately lightweight — heading + optional intro line
            + 4 check bullets + a soft CTA line. Renders only when
            the bucket ships a `journey` config. Every element carries
            a data-testid so future per-bucket variants can be
            measured. */}
        {_journey && (
          <div
            ref={journeyRef}
            data-testid="activation-journey"
            data-landing-page-id={landingPageId}
            className="mb-4 rounded-2xl border border-emerald-900/40 bg-emerald-950/[0.18] px-4 py-4"
          >
            <h2
              data-testid="activation-journey-heading"
              className="text-[15px] sm:text-[16px] leading-snug font-semibold text-emerald-100 tracking-tight"
            >
              {_journey.heading}
            </h2>
            {_journey.body_intro && (
              <p
                data-testid="activation-journey-intro"
                className="mt-1.5 text-[12.5px] sm:text-[13px] leading-relaxed text-neutral-300"
              >
                {_journey.body_intro}
              </p>
            )}
            <ul
              data-testid="activation-journey-bullets"
              className="mt-2 space-y-1"
            >
              {(_journey.bullets || []).map((b, i) => (
                <li
                  key={i}
                  data-testid={`activation-journey-bullet-${i}`}
                  className="flex items-start gap-2 text-[13px] sm:text-[13.5px] leading-relaxed text-neutral-200"
                >
                  <span
                    aria-hidden="true"
                    className="mt-[3px] inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 flex-none shadow-[0_0_6px_rgba(52,211,153,0.45)]"
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            {/* Iter XX.121 — Merge Trust Into Journey. Understated
                inline reassurance — NOT a testimonial. Sits inside
                the SAME journey card between the bullet list and
                the CTA. No card-in-card, no stars, no marketing
                colour. A thin divider separates it from the bullets;
                the muted quote icon signals "this is another
                conversation, not a claim". Renders only when the
                bucket configures `journey.trustStory`. */}
            {_journey.trustStory && _journey.trustStory.quote && (
              <div
                data-testid="activation-journey-trust"
                data-attribution={_journey.trustStory.attribution || ""}
                className="mt-4 pt-3 border-t border-emerald-900/40"
              >
                <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] text-neutral-500">
                  <span aria-hidden="true" className="text-neutral-600">💬</span>
                  <span>From a Gully player</span>
                </div>
                <blockquote
                  data-testid="activation-journey-trust-quote"
                  className="mt-1.5 text-[13px] sm:text-[13.5px] leading-relaxed text-neutral-200 italic"
                >
                  “{_journey.trustStory.quote}”
                </blockquote>
                {_journey.trustStory.attribution && (
                  <p
                    data-testid="activation-journey-trust-attribution"
                    className="mt-1 text-[11.5px] leading-snug text-neutral-500"
                  >
                    — {_journey.trustStory.attribution}
                  </p>
                )}
              </div>
            )}
            {_journey.cta_line && (
              <p
                data-testid="activation-journey-cta"
                className="mt-3 text-[12.5px] sm:text-[13px] leading-snug text-emerald-300/90 font-medium"
              >
                {_journey.cta_line}
              </p>
            )}
          </div>
        )}

        {/* Iter XX.110 — Founder trust row. Subtle attribution kept
            at the bottom of the page (Iter XX.122 kept it under the
            journey card so the composer stays clean above the fold). */}
        <div
          data-testid="activation-founder-row"
          className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2 text-[11px] leading-tight text-neutral-500"
        >
          <span data-testid="activation-founder-name" className="flex items-center gap-1">
            <span aria-hidden="true">👤</span>
            <span className="text-neutral-300">{FOUNDER_NAME}</span>
          </span>
          <span aria-hidden="true" className="text-neutral-700">·</span>
          <span data-testid="activation-founder-role">{FOUNDER_ROLE}</span>
          <span aria-hidden="true" className="text-neutral-700">·</span>
          <a
            href={FOUNDER_INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="activation-founder-instagram"
            onClick={() => {
              try {
                track("founder_instagram_click", {
                  landing_variant: LANDING_VARIANT,
                  landing_page_id: landingPageId,
                  source: "trust_row",
                });
              } catch { /* noop */ }
            }}
            className="flex items-center gap-1 text-neutral-400 hover:text-neutral-200 underline decoration-neutral-700 underline-offset-2 transition-colors"
          >
            <span aria-hidden="true">📷</span>
            <span>{FOUNDER_HANDLE}</span>
          </a>
        </div>
        {/* Android 1.6 live on Google Play 2026-10-03 (public listing verified).
            iOS badge stays hidden until the App Store public listing confirms 1.6. */}
        <div
          data-testid="activation-store-badges"
          className="flex justify-center mt-2 mb-2"
        >
          <a
            href="https://play.google.com/store/apps/details?id=com.gullyapp.mobile"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="activation-cta-google-play"
            onClick={() => {
              try {
                track("google_play_click", {
                  landing_variant: LANDING_VARIANT,
                  landing_page_id: landingPageId,
                  source: "trust_row",
                });
              } catch { /* noop */ }
            }}
            className="inline-flex items-center gap-1 text-neutral-400 hover:text-neutral-200 underline decoration-neutral-700 underline-offset-2 transition-colors text-[11px]"
          >
            Get it on Google Play
          </a>
        </div>
        {/* Iter XX.143 — closes `activation-below-fold`. */}
        </div>
      </main>
      {/* Iter XX.96 — Parent Entry gate rendered at the commitment
          moment. The visitor's original intent is preserved in
          pendingIntentRef and resumed automatically once the gate
          completes (see _onParentGateComplete). */}
      {showParentGate && (
        <ParentEntryGate
          sessionId={parentGateSessionId}
          variant="commitment"
          onComplete={_onParentGateComplete}
        />
      )}
    </div>
  );
}
